import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

/**
 * Deep-linkable modals: `?show=<kind>&id=<id>` on a page opens that page's modal for one item.
 *
 * Opening a modal by a click writes the two params into the URL (shallow replace, no reload),
 * closing it removes them, so the address bar is always a shareable link to what is on screen.
 * A cold load with the params fetches the item by id and opens the modal. The same URLs work as
 * push notification targets and as app links, since both route by in-app path.
 *
 * Only one modal is linked at a time. On a page with a dynamic `[id]` route segment the URL is
 * left alone (Next.js merges route params into `router.query`, so the route's `id` would clash)
 * and the modal simply opens without a link.
 */
export const SHOW_PARAM = "show";
export const ID_PARAM = "id";

const OPEN_EVENT = "ortex:open-modal-link";

const first = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value) || "";

/** The query a link to one item adds to a page: `{ show: kind, id }`. */
export const modalQuery = (kind: string, id: string | number) => ({ [SHOW_PARAM]: kind, [ID_PARAM]: String(id) });

/**
 * Opens the app-wide modal of `kind` (one mounted with `useModalLink(kind, …, { global: true })`) from anywhere.
 * `value` shows the modal at once instead of waiting for the loader (as `open(id, value)` does).
 */
export const openModalLink = (kind: string, id: string | number, value?: unknown) => {
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { kind, id: String(id), value } }));
};

export const useModalLink = <T>(kind: string, load: (id: string, signal: AbortSignal) => Promise<T>, options: { global?: boolean } = {}) => {
    const router = useRouter();
    const urlable = !router.pathname.includes("[id]");
    const [localId, setLocalId] = useState("");
    const linkedId = urlable ? (router.isReady && first(router.query[SHOW_PARAM]) === kind ? first(router.query[ID_PARAM]) : "") : localId;
    const [item, setItem] = useState<{ id: string; value: T } | null>(null);
    const [loading, setLoading] = useState(false);
    // Opened by a click: shown at once, before the router has caught up with the new query.
    const pending = useRef<string | null>(null);
    const loadRef = useRef(load);
    loadRef.current = load;

    const stripQuery = useCallback(() => {
        const query = { ...router.query };
        delete query[SHOW_PARAM];
        delete query[ID_PARAM];
        return query;
    }, [router]);

    const close = useCallback(() => {
        pending.current = null;
        setItem(null);
        setLocalId("");
        if (!urlable || first(router.query[SHOW_PARAM]) !== kind) return;
        router.replace({ pathname: router.pathname, query: stripQuery() }, undefined, { shallow: true, scroll: false });
    }, [kind, router, stripQuery, urlable]);

    /** Shows the item and puts its link in the URL. `extra` adds params the link needs (e.g. a tab). */
    const open = useCallback(
        (id: string | number, value?: T, extra?: Record<string, string>) => {
            const key = String(id);
            if (value !== undefined) {
                pending.current = key;
                setItem({ id: key, value });
            }
            if (!urlable) {
                setLocalId(key);
                return;
            }
            router.replace({ pathname: router.pathname, query: { ...stripQuery(), ...extra, ...modalQuery(kind, key) } }, undefined, { shallow: true, scroll: false });
        },
        [kind, router, stripQuery, urlable]
    );

    // An app-wide modal also opens on openModalLink(kind, id) from any component.
    const openRef = useRef(open);
    openRef.current = open;
    useEffect(() => {
        if (!options.global) return;
        const onOpen = (event: Event) => {
            const detail = (event as CustomEvent<{ kind: string; id: string; value?: T }>).detail;
            if (detail?.kind === kind && detail.id) openRef.current(detail.id, detail.value);
        };
        window.addEventListener(OPEN_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_EVENT, onOpen);
    }, [kind, options.global]);

    useEffect(() => {
        if (urlable && !router.isReady) return;
        if (!linkedId) {
            // A click open whose URL update has not landed yet keeps its item.
            if (!pending.current) setItem(null);
            return;
        }
        pending.current = null;
        if (item?.id === linkedId) return;
        const controller = new AbortController();
        setLoading(true);
        loadRef
            .current(linkedId, controller.signal)
            .then((value) => {
                // Loaders without the signal still resolve after an abort: a late answer must not replace a newer item.
                if (!controller.signal.aborted) setItem({ id: linkedId, value });
            })
            .catch(() => {
                // Gone, or not this reader's to see: drop the dead link rather than show an empty modal.
                if (!controller.signal.aborted) close();
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedId, router.isReady]);

    const visible = item && (item.id === linkedId || item.id === pending.current) ? item.value : null;
    // `linked`: the URL carries the item, so a share button has a link to share.
    return { item: visible, id: linkedId || pending.current || null, loading, open, close, linked: urlable };
};

export default useModalLink;
